/**
 * CoConnect — FarmPolygonViewer (v4.4.18 — Enterprise 10/10)
 *
 * Read-only Leaflet map with:
 *   - Esri World Imagery (0.3m satellite, free, no key)
 *   - Layer switcher (Satellite / Street / Google) with SVG icons
 *   - Scale bar (metric + imperial)
 *   - North arrow compass (rotates with map — though Leaflet doesn't rotate, it's a visual reference)
 *   - Fullscreen button
 *   - Mouse coordinate readout (lat/lng on hover)
 *   - Geolocation "find me" button
 *   - Print / export (GeoJSON download + print)
 *   - Professional SVG location pin for centroid
 *   - Usage instructions overlay
 *   - ResizeObserver for proper invalidateSize
 *   - Tile error handling with automatic provider failover
 *   - Attribution compliance
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import { createSemanticTileController, type SemanticTileController } from '../services/googleMaps';

interface GeoJsonPolygon { type: 'Polygon'; coordinates: number[][][] }

export interface FarmPolygonViewerProps {
  polygon?: GeoJsonPolygon | null;
  centroid?: { lat: number; lng: number } | null;
  areaHa?: number;
  height?: string;
  active?: boolean;
  label?: string;
  // v4.4.19: EUDR compliance overlay
  eudrStatus?: string;   // 'UNMAPPED' | 'PENDING' | 'VERIFIED' | 'REJECTED' | 'EXPIRED'
  eudrRiskLevel?: string; // 'low' | 'medium' | 'high'
}

const DEFAULT_CENTER: [number, number] = [4.6333, 9.4500]; // Cameroon

const FarmPolygonViewer: React.FC<FarmPolygonViewerProps> = ({
  polygon, centroid, areaHa, height = '320px', active = true, label,
  eudrStatus, eudrRiskLevel,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const polygonLayerRef = useRef<L.Polygon | null>(null);
  const markerLayerRef = useRef<L.Marker | null>(null);
  const tileControllerRef = useRef<SemanticTileController | null>(null);
  const [layerType, setLayerType] = useState<'satellite' | 'street' | 'google'>('satellite');
  const [coords, setCoords] = useState<string>('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const installTiles = useCallback((type: 'satellite' | 'street' | 'google') => {
    const map = mapRef.current;
    if (!map) return;
    tileControllerRef.current?.destroy();
    tileControllerRef.current = createSemanticTileController(map, type);
  }, []);

  // ─── Initialize the map ───
  useEffect(() => {
    if (!active || !containerRef.current || mapRef.current) return;
    const initialCenter: [number, number] = centroid ? [centroid.lat, centroid.lng] : DEFAULT_CENTER;
    const map = L.map(containerRef.current, {
      center: initialCenter, zoom: 15, zoomControl: true, attributionControl: true,
    });

    mapRef.current = map;
    installTiles('satellite');

    // v4.4.18: Scale bar (metric + imperial)
    L.control.scale({ position: 'bottomleft', metric: true, imperial: true }).addTo(map);

    // v4.4.18: Mouse coordinate readout
    map.on('mousemove', (e: L.LeafletMouseEvent) => {
      setCoords(`${e.latlng.lat.toFixed(5)}, ${e.latlng.lng.toFixed(5)}`);
    });
    map.on('mouseout', () => setCoords(''));


    // v4.4.20: Geometry-safe resize handling. Leaflet must be invalidated only
    // after the actual map viewport dimensions change; otherwise mobile drawer,
    // orientation and responsive-layout transitions can leave stale tile geometry.
    let lastWidth = 0;
    let lastHeight = 0;
    let resizeRaf = 0;
    const resizeObserver = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      if (Math.abs(rect.width - lastWidth) < 0.5 && Math.abs(rect.height - lastHeight) < 0.5) return;
      lastWidth = rect.width;
      lastHeight = rect.height;
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => mapRef.current?.invalidateSize({ pan: false }));
    });
    resizeObserver.observe(containerRef.current);
    const layoutInvalidate = () => requestAnimationFrame(() => mapRef.current?.invalidateSize({ pan: false }));
    window.addEventListener('coconnect:layout-resize', layoutInvalidate as EventListener);
    window.addEventListener('resize', layoutInvalidate);
    const initialRaf = requestAnimationFrame(() => map.invalidateSize({ pan: false }));
    const initialTimer = window.setTimeout(() => map.invalidateSize({ pan: false }), 180);

    return () => {
      window.removeEventListener('coconnect:layout-resize', layoutInvalidate as EventListener);
      window.removeEventListener('resize', layoutInvalidate);
      resizeObserver.disconnect();
      cancelAnimationFrame(resizeRaf);
      cancelAnimationFrame(initialRaf);
      window.clearTimeout(initialTimer);
      tileControllerRef.current?.destroy();
      tileControllerRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  // v4.4.18: Layer switcher with proper cleanup
  const switchLayer = useCallback((type: 'satellite' | 'street' | 'google') => {
    if (!mapRef.current) return;
    installTiles(type);
    setLayerType(type);
  }, [installTiles]);

  // v4.4.18: Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!wrapperRef.current) return;
    if (!document.fullscreenElement) {
      wrapperRef.current.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  // v4.4.18: Geolocation "find me"
  const findMe = useCallback(() => {
    if (!navigator.geolocation || !mapRef.current) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        mapRef.current?.setView([pos.coords.latitude, pos.coords.longitude], 17);
      },
      () => alert('Could not get your location. Check GPS permissions.'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  // v4.4.18: Export GeoJSON
  const exportGeoJSON = useCallback(() => {
    if (!polygon) return;
    const geojson = { type: 'FeatureCollection', features: [{ type: 'Feature', geometry: polygon, properties: { name: label || 'Farm', area_ha: areaHa } }] };
    const blob = new Blob([JSON.stringify(geojson, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(label || 'farm').replace(/\s+/g, '_')}_boundary.geojson`;
    a.click();
    URL.revokeObjectURL(url);
  }, [polygon, label, areaHa]);

  // v4.4.18: Print map
  const printMap = useCallback(() => { window.print(); }, []);

  // ─── Re-render polygon ───
  const safeAreaHa = areaHa != null ? Number(areaHa) : null;
  const safeCentroid = centroid ? { lat: Number(centroid.lat), lng: Number(centroid.lng) } : null;

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !polygon) return;
    if (polygonLayerRef.current) { map.removeLayer(polygonLayerRef.current); polygonLayerRef.current = null; }
    if (markerLayerRef.current) { map.removeLayer(markerLayerRef.current); markerLayerRef.current = null; }

    const pts = (polygon.coordinates[0] as any).map(([lng, lat]: [number, number]) => [lat, lng] as [number, number]);
    if (pts.length < 3) return;

    // v4.4.18: Polygon validation — check for self-intersection (simple crossing check)
    let hasIntersection = false;
    for (let i = 0; i < pts.length - 1; i++) {
      for (let j = i + 2; j < pts.length - 1; j++) {
        if (segmentsIntersect(pts[i], pts[i + 1], pts[j], pts[j + 1])) {
          hasIntersection = true; break;
        }
      }
      if (hasIntersection) break;
    }

    // v4.4.19: EUDR compliance coloring — the polygon fill color reflects EUDR status
    // VERIFIED = green, PENDING = amber, REJECTED/EXPIRED = red, UNMAPPED = gray
    const eudrColors: Record<string, { color: string; fillColor: string; label: string }> = {
      VERIFIED:  { color: '#15803D', fillColor: '#22c55e', label: 'EUDR Verified' },
      PENDING:   { color: '#D97706', fillColor: '#fbbf24', label: 'EUDR Pending' },
      REJECTED:  { color: '#dc2626', fillColor: '#ef4444', label: 'EUDR Rejected' },
      EXPIRED:   { color: '#dc2626', fillColor: '#ef4444', label: 'EUDR Expired' },
      UNMAPPED:  { color: '#6b7280', fillColor: '#9ca3af', label: 'Not Mapped' },
    };
    const eudrStyle = eudrStatus ? eudrColors[eudrStatus] || eudrColors.UNMAPPED : eudrColors.UNMAPPED;

    // If self-intersecting, override to red dashed
    const polyColor = hasIntersection ? '#dc2626' : eudrStyle.color;
    const polyFill = hasIntersection ? '#dc2626' : eudrStyle.fillColor;

    const poly = L.polygon(pts, {
      color: polyColor,
      weight: 3,
      fillColor: polyFill,
      fillOpacity: 0.25,
      dashArray: hasIntersection ? '6 4' : (eudrStatus === 'PENDING' || eudrStatus === 'EXPIRED' ? '8 4' : undefined),
    }).addTo(map);
    polygonLayerRef.current = poly;

    try { map.fitBounds(poly.getBounds(), { padding: [30, 30] }); } catch { /* ignore */ }

    if (safeCentroid) {
      const pinIcon = L.divIcon({
        html: `<div style="position: relative; width: 32px; height: 40px; transform: translateY(-20px);">
          <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 0C7.16 0 0 7.16 0 16c0 12 16 24 16 24s16-12 16-24c0-8.84-7.16-16-16-16z" fill="#1e40af" stroke="#fff" stroke-width="2"/>
            <circle cx="16" cy="16" r="6" fill="#fff"/><circle cx="16" cy="16" r="3" fill="#1e40af"/>
          </svg></div>`,
        className: 'farm-centroid-pin', iconSize: [32, 40], iconAnchor: [16, 40],
      });
      const marker = L.marker([safeCentroid.lat, safeCentroid.lng], { icon: pinIcon }).addTo(map);
      marker.bindPopup(
        `<div style="font-family: Inter, sans-serif; font-size: 13px; line-height: 1.5;">
           <strong style="font-size: 14px; color: #1e40af;">${label || 'Farm'}</strong><br>
           ${safeAreaHa ? `<span style="color: #15803D; font-weight: 600;">Area: ${safeAreaHa.toFixed(2)} ha</span><br>` : ''}
           <span style="color: #666; font-size: 11px;">GPS: ${safeCentroid.lat.toFixed(5)}, ${safeCentroid.lng.toFixed(5)}</span>
           ${hasIntersection ? '<br><span style="color: #dc2626; font-weight: 600;">⚠ Boundary self-intersects!</span>' : ''}
         </div>`
      );
      markerLayerRef.current = marker;
    }
  }, [polygon, safeCentroid, safeAreaHa, label]);

  // v4.4.20: Reconcile Leaflet geometry after visibility, viewport,
  // orientation, fullscreen and app-shell changes. ResizeObserver handles direct
  // container changes; these listeners cover browser-level transitions.
  useEffect(() => {
    if (!active) return;
    let raf = 0;
    let timer = 0;
    const refresh = () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
      raf = requestAnimationFrame(() => mapRef.current?.invalidateSize({ pan: false }));
      timer = window.setTimeout(() => mapRef.current?.invalidateSize({ pan: false }), 140);
    };
    const onVisibility = () => { if (!document.hidden) refresh(); };
    refresh();
    window.addEventListener('resize', refresh, { passive: true });
    window.addEventListener('orientationchange', refresh, { passive: true });
    document.addEventListener('fullscreenchange', refresh);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
      window.removeEventListener('resize', refresh);
      window.removeEventListener('orientationchange', refresh);
      document.removeEventListener('fullscreenchange', refresh);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [active]);

  return (
    <div ref={wrapperRef} className="farmer-map-viewer relative w-full" style={{ height }}>
      <div ref={containerRef} className="farmer-map-canvas absolute inset-0 rounded-xl overflow-hidden" />

      {/* Farm name label */}
      {label && (
        <div className="farmer-map-label absolute top-2 left-2 z-[1000] px-2.5 py-1 rounded-md bg-white/95 dark:bg-ink-900/95 backdrop-blur text-xs font-semibold text-ink-900 dark:text-ink-50 shadow-sm border border-ink-200 dark:border-ink-800">
          {label}
        </div>
      )}

      {/* Area badge + EUDR status badge */}
      <div className="farmer-map-status absolute bottom-2 right-2 z-[1000] flex flex-col gap-1 items-end">
        {safeAreaHa != null && safeAreaHa > 0 && (
          <div className="px-2.5 py-1 rounded-md bg-white/95 dark:bg-ink-900/95 backdrop-blur text-2xs font-medium text-success-700 dark:text-success-500 shadow-sm border border-ink-200 dark:border-ink-800 nums">
            {safeAreaHa.toFixed(2)} ha
          </div>
        )}
        {/* v4.4.19: EUDR compliance badge */}
        {eudrStatus && (
          <div className={`px-2.5 py-1 rounded-md backdrop-blur text-2xs font-bold shadow-sm border flex items-center gap-1 ${
            eudrStatus === 'VERIFIED' ? 'bg-success-50 dark:bg-success-500/10 text-success-700 dark:text-success-500 border-success-200 dark:border-success-500/20' :
            eudrStatus === 'PENDING' ? 'bg-warning-50 dark:bg-warning-500/10 text-warning-700 dark:text-warning-500 border-warning-200 dark:border-warning-500/20' :
            eudrStatus === 'REJECTED' || eudrStatus === 'EXPIRED' ? 'bg-danger-50 dark:bg-danger-500/10 text-danger-700 dark:text-danger-500 border-danger-200 dark:border-danger-500/20' :
            'bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-400 border-ink-200 dark:border-ink-700'
          }`}>
            {eudrStatus === 'VERIFIED' && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            )}
            {eudrStatus === 'PENDING' && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            )}
            {(eudrStatus === 'REJECTED' || eudrStatus === 'EXPIRED') && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
            )}
            EUDR: {eudrStatus}
            {eudrRiskLevel && <span className="opacity-70">· Risk: {eudrRiskLevel}</span>}
          </div>
        )}
      </div>

      {/* v4.4.18: Layer switcher with SVG icons */}
      <div className="farmer-map-layers absolute top-2 right-2 z-[1000] flex flex-col gap-1">
        {(['satellite', 'street', 'google'] as const).map((type) => (
          <button
            key={type}
            onClick={() => switchLayer(type)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-2xs font-semibold shadow-md border transition-all ${layerType === type ? 'bg-admin-600 text-white border-admin-700 shadow-lg' : 'bg-white/95 dark:bg-ink-900/95 text-ink-700 dark:text-ink-300 border-ink-200 dark:border-ink-800 hover:bg-white'}`}
            title={type === 'satellite' ? 'Esri World Imagery (0.3m satellite)' : type === 'street' ? 'OpenStreetMap street map' : 'Google Hybrid (requires API key)'}
            aria-label={`Switch to ${type} map`}
          >
            {type === 'satellite' && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
            )}
            {type === 'street' && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>
            )}
            {type === 'google' && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><circle cx="12" cy="12" r="3" fill="currentColor"/></svg>
            )}
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        ))}
      </div>

      {/* v4.4.18: Action buttons (fullscreen, find me, export, print) */}
      <div className="farmer-map-actions absolute top-2 left-1/2 -translate-x-1/2 z-[1000] flex gap-1">
        <button onClick={findMe} aria-label="Find my location" className="w-8 h-8 flex items-center justify-center bg-white/95 dark:bg-ink-900/95 rounded-md shadow-md border border-ink-200 dark:border-ink-800 text-ink-600 dark:text-ink-300 hover:bg-white transition-colors" title="Find my location">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/></svg>
        </button>
        <button onClick={toggleFullscreen} aria-label="Open map fullscreen" className="w-8 h-8 flex items-center justify-center bg-white/95 dark:bg-ink-900/95 rounded-md shadow-md border border-ink-200 dark:border-ink-800 text-ink-600 dark:text-ink-300 hover:bg-white transition-colors" title="Fullscreen">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3"/></svg>
        </button>
        {polygon && (
          <button onClick={exportGeoJSON} aria-label="Export farm boundary as GeoJSON" className="w-8 h-8 flex items-center justify-center bg-white/95 dark:bg-ink-900/95 rounded-md shadow-md border border-ink-200 dark:border-ink-800 text-ink-600 dark:text-ink-300 hover:bg-white transition-colors" title="Export GeoJSON">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
          </button>
        )}
        <button onClick={printMap} aria-label="Print farm map" className="w-8 h-8 flex items-center justify-center bg-white/95 dark:bg-ink-900/95 rounded-md shadow-md border border-ink-200 dark:border-ink-800 text-ink-600 dark:text-ink-300 hover:bg-white transition-colors" title="Print">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
        </button>
      </div>

      {/* v4.4.18: North arrow compass */}
      <div className="farmer-map-compass absolute bottom-12 right-2 z-[1000] w-10 h-10 flex items-center justify-center bg-white/95 dark:bg-ink-900/95 rounded-full shadow-md border border-ink-200 dark:border-ink-800">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 2 L16 12 L12 10 L8 12 Z" fill="#dc2626" stroke="#fff" strokeWidth="1"/>
          <path d="M12 22 L8 12 L12 14 L16 12 Z" fill="#374151" stroke="#fff" strokeWidth="1"/>
          <text x="12" y="8" textAnchor="middle" fontSize="6" fill="#dc2626" fontWeight="bold">N</text>
        </svg>
      </div>

      {/* v4.4.18: Coordinate readout */}
      {coords && (
        <div className="farmer-map-coordinates absolute bottom-2 left-1/2 -translate-x-1/2 z-[1000] px-2.5 py-1 rounded-md bg-white/95 dark:bg-ink-900/95 backdrop-blur text-2xs font-mono text-ink-600 dark:text-ink-400 shadow-sm border border-ink-200 dark:border-ink-800">
          {coords}
        </div>
      )}

      {/* v4.4.18: Usage instructions */}
      <div className="farmer-map-help absolute bottom-12 left-2 z-[1000] max-w-[200px] px-3 py-2 rounded-lg bg-white/90 dark:bg-ink-900/90 backdrop-blur text-2xs text-ink-600 dark:text-ink-400 shadow-md border border-ink-200 dark:border-ink-800">
        <p className="font-semibold text-ink-800 dark:text-ink-200 mb-0.5">Map Controls</p>
        <p>• Top-right: switch layers</p>
        <p>• Top-center: locate, fullscreen, export, print</p>
        <p>• Bottom-left: scale bar</p>
        <p>• Hover: coordinates</p>
        {polygon && <p className="mt-0.5 text-success-700 dark:text-success-500 font-medium">Green = boundary ({safeAreaHa?.toFixed(2)} ha)</p>}
      </div>
    </div>
  );
};

// v4.4.18: Segment intersection check for polygon validation
function segmentsIntersect(p1: [number, number], p2: [number, number], p3: [number, number], p4: [number, number]): boolean {
  const d1 = cross(p3, p4, p1);
  const d2 = cross(p3, p4, p2);
  const d3 = cross(p1, p2, p3);
  const d4 = cross(p1, p2, p4);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return true;
  return false;
}
function cross(a: [number, number], b: [number, number], c: [number, number]): number {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
}

export default FarmPolygonViewer;
